import { CenterSpinner, HtmlRenderer, IconButton } from '@/components/ats';
import { EmailWithModal } from '@/components/ats/EmailTemplates/EmailWithModal';
import { AttachmentPreview } from '@/components/outreach-attachment/AttachmentPreview';
import { CustomScheduleAtV1 } from '@/components/ats/job-stage-actions/actions/email/CustomScheduleAtV1';
import { useApplicationHelper } from '@/components/candidate-job-slider-v1/hooks/useApplicationHelper';
import { useAuth } from '@/hooks';
import { useKFlags } from '@/hooks/use-k-flags';
import { useLazyGetPlaceholderPreviewQuery } from '@/store/query/email-templates-settings';
import { Box, Flex, Text, useDisclosure } from '@chakra-ui/react';
import { PlusIcon } from '@heroicons/react/solid';
import { useField } from 'formik';
import { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { EmailBodyFields } from '@/components/ats/EmailTemplates/EmailBodyFields';

const ShowEmailPreview = ({ isPreviewLoading, previewData, previewLabel }) => {
  return (
    <>
      <Text size="ats-sm">
        <Text as="span" size="ats-sm-bold" mr="4px">
          {previewLabel}:
        </Text>
        {isPreviewLoading ? 'Loading...' : previewData}
      </Text>
    </>
  );
};

export const RejectEmailPreview = ({
  formik,
  contact,
  templateOptions,
  initialValues = null,
  atsJobStageId,
}) => {
  const { jobId } = useParams();
  const contactJobID = contact?.ats_applications?.[0]?.ats_job_id;
  const [selectedTemplate, setSelectedTemplate] = useState({
    id: undefined,
    body: initialValues?.body || undefined,
    subject: initialValues?.subject || undefined,
    from: initialValues?.from || undefined,
    cc: initialValues?.cc || undefined,
    bcc: initialValues?.bcc || undefined,
    attachments: initialValues?.attachments || undefined,
    fromUserObject: initialValues?.fromUserObject || undefined,
  });
  const [previewTemplate, setPreviewTemplate] = useState({
    body: initialValues?.body || undefined,
    subject: initialValues?.subject || undefined,
  });
  const emailModalActions = useDisclosure();
  const [, { value }] = useField('selectedTemplate');

  // const [, { value: rejectEmail }] = useField('sendRejectEmail');
  const { values, setValues, setFieldValue } = formik || {};
  const { currUser } = useAuth();

  const { feRejectSchedule, feSharedEmails, feSurvey } = useKFlags();

  const { applicationData } = useApplicationHelper();
  const ats_job_id = jobId || contactJobID || applicationData?.ats_job?.id;

  const [
    getPlaceholderPreview,
    { data: previewData, isLoading: isPreviewLoading },
  ] = useLazyGetPlaceholderPreviewQuery();

  useEffect(() => {
    const { body, subject, id } = selectedTemplate || {};

    const fromUserObject = selectedTemplate?.fromUserObject ||
      initialValues?.fromUserObject || {
        id: currUser?.id,
        email: currUser?.email,
        name: `${currUser?.first_name ?? ''} ${
          currUser?.last_name ?? ''
        }`.trim(),
        avatar: currUser?.avatar,
      };

    if (id || body || subject) {
      setValues({
        ...values,
        body,
        subject,
        from: selectedTemplate?.from || initialValues?.from || currUser?.email,
        cc: selectedTemplate?.cc,
        bcc: selectedTemplate?.bcc,
        attachments: selectedTemplate?.attachments,
        ...(feSharedEmails ? { fromUserObject } : {}),
      });

      if (contact?.id && body && subject) {
        const payload = {
          body,
          subject,
          contact_id: contact.id,
          ats_job_id: ats_job_id,
          ats_job_stage_id: atsJobStageId,
        };

        // Fetch preview when template changes and contact ID is present
        getPlaceholderPreview({ payload });
      } else {
        // If no contact ID, use the current template as the preview
        setPreviewTemplate({ body, subject });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate, currUser, contact]);

  useEffect(() => {
    setSelectedTemplate((prev) => (prev !== value ? value : prev));
  }, [value]);

  useEffect(() => {
    if (previewData) {
      setPreviewTemplate(previewData);
    }
  }, [previewData]);

  const {
    body = initialValues?.body || '',
    subject = initialValues?.subject || '',
    // id,
    from = initialValues?.from || currUser?.email,
    cc = initialValues?.cc || [],
    bcc = initialValues?.bcc || [],
    attachments = initialValues?.attachments || [],
  } = selectedTemplate || {};

  const previewBody = previewTemplate?.body || body;
  const previewSubject = previewTemplate?.subject || subject;

  const selectedSurvey = useMemo(() => {
    const survey = applicationData?.ats_rejection_email?.survey || null;
    if (!survey) return null;
    return {
      value: survey?.ats_form_template?.id,
      label: survey?.ats_form_template?.title,
    };
  }, [applicationData]);

  if (!values?.sendRejectEmail) {
    return null;
  }

  if (!values?.selectedTemplate?.id) {
    // for edit case, if body is empty, return null
    if (!body && !values?.rejectEmailConfig?.body) {
      return null;
    }
  }

  const showPreviewData = [
    { label: 'From', value: values?.from },
    ...(contact?.email ? [{ label: 'To', value: contact.email }] : []),
    { label: 'Subject', value: previewSubject },
  ];

  return (
    <>
      <Flex
        flexDir="column"
        p={feSurvey ? '0px' : '12px 16px'}
        maxH={feSurvey ? 'unset' : '250px'}
        overflowY="auto"
        bg={feSurvey ? 'transparent' : 'ats-bg-tertiary'}
        borderRadius="sm"
      >
        <>
          {!feSurvey && (
            <Box mb="14px">
              {showPreviewData.map(({ label, value }) => (
                <ShowEmailPreview
                  key={label}
                  isPreviewLoading={isPreviewLoading}
                  previewData={value}
                  previewLabel={label}
                />
              ))}
            </Box>
          )}
          {isPreviewLoading ? (
            <CenterSpinner h="20vh" />
          ) : (
            <Box>
              {feSurvey && (
                <EmailBodyFields
                  formik={formik}
                  initialValues={{
                    body,
                    subject,
                    from,
                    cc,
                    bcc,
                    attachments,
                    ...initialValues,
                  }}
                  selectedSurvey={selectedSurvey}
                  onChange={(val) => {
                    setFieldValue('rejectEmailConfig', {
                      ...(formik?.values?.rejectEmailConfig || {}),
                      ...val,
                    });
                  }}
                  onSurveySelect={(val) => {
                    setFieldValue('rejectEmailConfig', {
                      ...(formik?.values?.rejectEmailConfig || {}),
                      ...{ survey: val },
                    });
                  }}
                  onSurveyRemove={() => {
                    setFieldValue('rejectEmailConfig', {
                      ...(formik?.values?.rejectEmailConfig || {}),
                      ...{ survey: null },
                    });
                  }}
                />
              )}
              {!feSurvey && (
                <>
                  <HtmlRenderer htmlString={previewBody} bg="transparent" />
                  {attachments && attachments.length > 0 && (
                    <Box mt="16px">
                      <Text size="ats-sm-bold" color="secondary-900" mb="8px">
                        Attachments:
                      </Text>
                      <Flex gap="8px" flexWrap="wrap">
                        {attachments.map((attachment, index) => (
                          <AttachmentPreview
                            key={attachment.id || index}
                            file={attachment}
                            bg="secondary-50"
                            AfterContent={undefined}
                          />
                        ))}
                      </Flex>
                    </Box>
                  )}
                </>
              )}
            </Box>
          )}
        </>
      </Flex>
      {!feSurvey && (
        <Flex>
          <IconButton
            type="button"
            icon={PlusIcon}
            text="Customize message"
            iconProps={{
              width: '12px',
              height: '12px',
              m: '4px',
            }}
            iconContainerProps={{
              width: 'unset',
              height: 'unset',
            }}
            mt="6px"
            data-test-id="customize-message"
            onClick={(e) => {
              e?.stopPropagation();
              emailModalActions?.onOpen();
            }}
          />
          {
            <EmailWithModal
              formik={formik}
              templateOptions={templateOptions}
              emailModalActions={emailModalActions}
              onSubmit={(val) => {
                setSelectedTemplate((prev) => ({
                  ...prev,
                  ...val,
                }));
                // Fetch preview for the updated template if contact ID is present
                if (contact?.id) {
                  getPlaceholderPreview({
                    body: val.body,
                    subject: val.subject,
                    contact_id: contact.id,
                    ats_job_id: applicationData?.ats_job?.id,
                  });
                } else {
                  // If no contact ID, use the updated template as the preview
                  setPreviewTemplate({ body: val.body, subject: val.subject });
                }
              }}
              initialValues={{
                body,
                subject,
                from: from || currUser?.email,
                cc,
                bcc,
                attachments,
                sendRejectEmail: initialValues?.sendRejectEmail,
              }}
            />
          }
        </Flex>
      )}
      {feRejectSchedule && (
        <Flex>
          <CustomScheduleAtV1 />
        </Flex>
      )}
    </>
  );
};
